import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  DatePicker,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import EChart from '../../components/charts/EChart';
import { useListFieldsQuery, useLazyListPlotsQuery } from '../../api/fieldsApi';
import { useLazyGetPlotPlanQuery, usePatchPlotPlanMutation } from '../../api/planApi';
import {
  useCreateGreenhouseClimatePeriodMutation,
  useCreateSimEventMutation,
  useDeleteSimEventMutation,
  useDeleteGreenhouseClimatePeriodMutation,
  useGetGreenhouseClimateStatusQuery,
  useGetGreenhouseClimateTimeseriesQuery,
  useListSimEventsQuery,
  useListGreenhouseClimatePeriodsQuery,
  useUpdateGreenhouseClimatePeriodMutation,
} from '../../api/simulationApi';
import type { StructuralObject } from '../../types/plan';
import type {
  GreenhouseHeatingSnapshot,
  GreenhouseClimatePeriod,
  SimEvent,
  UpsertGreenhouseClimatePeriodPayload,
} from '../../types/simulation';

const { Text } = Typography;

const GREENHOUSE_SUBTYPE_LABELS: Record<string, string> = {
  greenhouse: 'Теплица',
  parnik: 'Парник',
};

interface GreenhouseTarget {
  id: string;
  label: string;
  subtype: string;
  fieldId: string;
  fieldName: string;
  plotId: string;
  plotName: string;
  cellCount: number;
  activeFrom: string | null;
  activeTo: string | null;
}

interface GreenhouseClimatePanelProps {
  farmId: string;
  sessionId: string;
  saveId: string | null;
  simDate: string;
}

interface ClimatePeriodFormValues {
  startDate: Dayjs;
  endDate?: Dayjs | null;
  internalTempC?: number | null;
  internalTempAmplitudeC?: number | null;
  internalHumidityPct?: number | null;
  ventilationEnabled?: boolean;
  ventilationMaxC?: number | null;
  allowExternalPrecipitation: boolean;
  isIsolatedZone: boolean;
}

type HeatingEventAction = 'heating_on' | 'heating_off';

interface HeatingEventFormValues {
  eventDate: Dayjs;
  action: HeatingEventAction;
  mode: 'manual' | 'follow_tech_card';
  minC?: number | null;
  maxC?: number | null;
  ventilationMaxC?: number | null;
}

type SimEventLike = SimEvent & Record<string, unknown>;

const HEATING_MODE_LABELS: Record<string, string> = {
  manual: 'Ручной режим',
  follow_tech_card: 'По техкарте',
};

const HEATING_ACTION_LABELS: Record<HeatingEventAction, string> = {
  heating_on: 'Включение',
  heating_off: 'Выключение',
};

const greenhouseSubtypeLabel = (subtype?: string | null) => {
  if (!subtype) return 'Теплица';
  return GREENHOUSE_SUBTYPE_LABELS[subtype] || subtype;
};

const isGreenhouseObject = (structuralObject: StructuralObject) => (
  structuralObject.kind === 'covering' && ['greenhouse', 'parnik'].includes(structuralObject.subtype)
);

const formatOptionalNumber = (value: number | null | undefined, suffix = '') => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const normalized = Number(value);
  return `${normalized % 1 === 0 ? normalized.toFixed(0) : normalized.toFixed(1)}${suffix}`;
};

const formatTempBand = (temp: number | null | undefined, amplitude: number | null | undefined) => {
  if (temp == null) return '—';
  if (amplitude == null) return `${formatOptionalNumber(temp, '°C')}`;
  return `${formatOptionalNumber(temp, '°C')} ± ${formatOptionalNumber(amplitude, '°C')}`;
};

const buildClimatePayload = (
  values: ClimatePeriodFormValues,
  structuralObjectId: string,
): UpsertGreenhouseClimatePeriodPayload => ({
  structuralObjectId,
  startDate: values.startDate.format('YYYY-MM-DD'),
  endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
  internalTempC: values.internalTempC ?? null,
  internalTempAmplitudeC: values.internalTempAmplitudeC ?? null,
  internalHumidityPct: values.internalHumidityPct ?? null,
  ventilationEnabled: values.ventilationEnabled === true,
  ventilationMaxC: values.ventilationEnabled === true ? (values.ventilationMaxC ?? 35) : null,
  allowExternalPrecipitation: values.allowExternalPrecipitation,
  isIsolatedZone: values.isIsolatedZone,
});

const extractApiError = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const maybeAny = error as any;
    return maybeAny?.data?.message || maybeAny?.error || fallback;
  }
  return fallback;
};

const getHeatingSnapshot = (
  heating: GreenhouseHeatingSnapshot | null | undefined,
): GreenhouseHeatingSnapshot | null => {
  if (!heating || typeof heating !== 'object') return null;
  return heating;
};

const parseHeatingEventNotes = (event: SimEventLike): Record<string, unknown> | null => {
  const notes = event.notes;
  if (!notes) return null;
  if (typeof notes === 'object') {
    return notes as Record<string, unknown>;
  }
  if (typeof notes !== 'string') return null;
  try {
    const parsed = JSON.parse(notes);
    return typeof parsed === 'object' && parsed !== null
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

const getSimEventAction = (event: SimEventLike): string => {
  const raw = event.action ?? event.type;
  return typeof raw === 'string' ? raw.toLowerCase() : '';
};

const getSimEventStructuralObjectId = (event: SimEventLike): string => {
  const raw = event.structuralObjectId ?? event.structural_object_id;
  return raw == null ? '' : String(raw);
};

const buildHeatingSummary = (heating: GreenhouseHeatingSnapshot | null | undefined): string => {
  const snapshot = getHeatingSnapshot(heating);
  if (!snapshot) return 'Подогрев не активен';
  if (snapshot.mode === 'follow_tech_card') return 'Подогрев следует техкарте';

  const parts: string[] = [];
  if (snapshot.minC != null) parts.push(`min ${formatOptionalNumber(snapshot.minC, '°C')}`);
  if (snapshot.maxC != null) parts.push(`max ${formatOptionalNumber(snapshot.maxC, '°C')}`);
  if (snapshot.ventilationMaxC != null) parts.push(`вент. ${formatOptionalNumber(snapshot.ventilationMaxC, '°C')}`);
  return parts.length > 0 ? parts.join(' · ') : 'Ручной режим';
};

const buildHeatingFormDefaults = (
  currentDate: string,
  heating: GreenhouseHeatingSnapshot | null | undefined,
): Partial<HeatingEventFormValues> => {
  const snapshot = getHeatingSnapshot(heating);
  return {
    eventDate: dayjs(currentDate),
    action: 'heating_on',
    mode: snapshot?.mode === 'follow_tech_card' ? 'follow_tech_card' : 'manual',
    minC: snapshot?.minC ?? 5,
    maxC: snapshot?.maxC ?? 25,
    ventilationMaxC: snapshot?.ventilationMaxC ?? 35,
  };
};

const GreenhouseClimatePanel: React.FC<GreenhouseClimatePanelProps> = ({ farmId, sessionId, saveId, simDate }) => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm<ClimatePeriodFormValues>();
  const [heatingForm] = Form.useForm<HeatingEventFormValues>();

  const { data: fields, isFetching: loadingFields } = useListFieldsQuery(farmId, {
    skip: !farmId,
  });
  const [triggerListPlots] = useLazyListPlotsQuery();
  const [triggerGetPlotPlan] = useLazyGetPlotPlanQuery();

  const [targets, setTargets] = useState<GreenhouseTarget[]>([]);
  const [discoveringTargets, setDiscoveringTargets] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<GreenhouseClimatePeriod | null>(null);

  const [createPeriod, { isLoading: creatingPeriod }] = useCreateGreenhouseClimatePeriodMutation();
  const [updatePeriod, { isLoading: updatingPeriod }] = useUpdateGreenhouseClimatePeriodMutation();
  const [deletePeriod, { isLoading: deletingPeriod }] = useDeleteGreenhouseClimatePeriodMutation();
  const [patchPlotPlan, { isLoading: patchingWindow }] = usePatchPlotPlanMutation();
  const [createSimEvent, { isLoading: creatingHeatingEvent }] = useCreateSimEventMutation();
  const [deleteSimEvent, { isLoading: deletingHeatingEvent }] = useDeleteSimEventMutation();

  const fieldIdsKey = useMemo(
    () => (fields || []).map((field) => field.id).sort().join('|'),
    [fields],
  );

  useEffect(() => {
    if (!fields?.length) {
      setTargets([]);
      return;
    }

    let cancelled = false;
    const loadTargets = async () => {
      setDiscoveringTargets(true);
      try {
        const plotsByField = await Promise.all(
          fields.map(async (field) => {
            const plots = await triggerListPlots(field.id, true).unwrap().catch(() => []);
            return { field, plots };
          }),
        );

        const plansByPlot = await Promise.all(
          plotsByField.flatMap(({ field, plots }) =>
            plots.map(async (plot) => {
              const plan = saveId
                ? await triggerGetPlotPlan({ saveId, plotId: plot.id, fieldId: field.id }, true)
                .unwrap()
                .catch(() => null)
                : null;
              return { field, plot, plan };
            }),
          ),
        );

        const nextTargets = plansByPlot
          .flatMap(({ field, plot, plan }) => {
            if (!plan?.structuralObjects?.length) return [];
            return plan.structuralObjects
              .filter(isGreenhouseObject)
              .map((structuralObject) => {
                const cellCount = (plan.cells || []).filter(
                  (cell) => cell.structuralObjectId === structuralObject.id,
                ).length;
                const baseLabel = structuralObject.name?.trim() || greenhouseSubtypeLabel(structuralObject.subtype);
                return {
                  id: structuralObject.id,
                  label: `${baseLabel} · ${field.name} / ${plot.name}`,
                  subtype: structuralObject.subtype,
                  fieldId: field.id,
                  fieldName: field.name,
                  plotId: plot.id,
                  plotName: plot.name,
                  cellCount,
                  activeFrom: structuralObject.activeFrom ?? null,
                  activeTo: structuralObject.activeTo ?? null,
                } satisfies GreenhouseTarget;
              });
          })
          .sort((left, right) => left.label.localeCompare(right.label, 'ru'));

        if (!cancelled) {
          setTargets(nextTargets);
        }
      } finally {
        if (!cancelled) {
          setDiscoveringTargets(false);
        }
      }
    };

    loadTargets();
    return () => {
      cancelled = true;
    };
  }, [farmId, fieldIdsKey, fields, saveId, triggerGetPlotPlan, triggerListPlots]);

  useEffect(() => {
    if (!targets.length) {
      setSelectedTargetId('');
      return;
    }
    if (!targets.some((target) => target.id === selectedTargetId)) {
      setSelectedTargetId(targets[0].id);
    }
  }, [selectedTargetId, targets]);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.id === selectedTargetId) || null,
    [selectedTargetId, targets],
  );

  const { data: periodsData, isFetching: loadingPeriods } = useListGreenhouseClimatePeriodsQuery(
    { sessionId, structuralObjectId: selectedTargetId },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: climateStatus, isFetching: loadingStatus } = useGetGreenhouseClimateStatusQuery(
    { sessionId, structuralObjectId: selectedTargetId, date: simDate },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: climateTimeseries, isFetching: loadingTimeseries } = useGetGreenhouseClimateTimeseriesQuery(
    { sessionId, structuralObjectId: selectedTargetId },
    { skip: !sessionId || !selectedTargetId },
  );

  const { data: heatingEventsData, isFetching: loadingHeatingEvents } = useListSimEventsQuery(
    { saveId: saveId || '', sessionId, structuralObjectId: selectedTargetId },
    { skip: !saveId || !selectedTargetId },
  );

  const periods = periodsData?.periods || [];
  const busyMutation = creatingPeriod || updatingPeriod || deletingPeriod;
  const currentHeating = useMemo(
    () => getHeatingSnapshot(climateStatus?.layers.heating || climateStatus?.heating),
    [climateStatus?.heating, climateStatus?.layers.heating],
  );
  const heatingAction = Form.useWatch('action', heatingForm);
  const heatingMode = Form.useWatch('mode', heatingForm);
  const heatingEvents = useMemo(
    () => ((heatingEventsData?.events || []) as SimEventLike[])
      .filter((event) => ['heating_on', 'heating_off'].includes(getSimEventAction(event)))
      .filter((event) => !selectedTargetId || getSimEventStructuralObjectId(event) === selectedTargetId)
      .slice()
      .sort((left, right) => String(right.date || '').localeCompare(String(left.date || ''))),
    [heatingEventsData?.events, selectedTargetId],
  );

  useEffect(() => {
    heatingForm.setFieldsValue(buildHeatingFormDefaults(simDate, currentHeating));
  }, [currentHeating, heatingForm, selectedTargetId, simDate]);

  const openCreateModal = () => {
    setEditingPeriod(null);
    form.setFieldsValue({
      startDate: dayjs(simDate),
      endDate: null,
      internalTempC: undefined,
      internalTempAmplitudeC: undefined,
      internalHumidityPct: undefined,
      ventilationEnabled: false,
      ventilationMaxC: 35,
      allowExternalPrecipitation: false,
      isIsolatedZone: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = useCallback((period: GreenhouseClimatePeriod) => {
    setEditingPeriod(period);
    form.setFieldsValue({
      startDate: dayjs(period.startDate),
      endDate: period.endDate ? dayjs(period.endDate) : null,
      internalTempC: period.internalTempC ?? undefined,
      internalTempAmplitudeC: period.internalTempAmplitudeC ?? undefined,
      internalHumidityPct: period.internalHumidityPct ?? undefined,
      ventilationEnabled: period.ventilationEnabled === true,
      ventilationMaxC: period.ventilationMaxC ?? 35,
      allowExternalPrecipitation: period.allowExternalPrecipitation,
      isIsolatedZone: period.isIsolatedZone,
    });
    setIsModalOpen(true);
  }, [form]);

  const handleSubmit = async () => {
    if (!selectedTargetId) return;
    const values = await form.validateFields();
    const payload = buildClimatePayload(values, selectedTargetId);

    if (payload.internalTempC == null && payload.internalHumidityPct == null) {
      form.setFields([
        { name: 'internalTempC', errors: ['Нужно указать температуру или влажность'] },
        { name: 'internalHumidityPct', errors: ['Нужно указать температуру или влажность'] },
      ]);
      return;
    }

    if (payload.internalTempAmplitudeC != null && payload.internalTempC == null) {
      form.setFields([
        { name: 'internalTempAmplitudeC', errors: ['Амплитуда допустима только вместе с базовой температурой'] },
      ]);
      return;
    }

    try {
      if (editingPeriod) {
        await updatePeriod({
          sessionId,
          periodId: editingPeriod.id,
          structuralObjectId: selectedTargetId,
          payload,
        }).unwrap();
        messageApi.success('Период климата обновлён');
      } else {
        await createPeriod({
          sessionId,
          payload,
        }).unwrap();
        messageApi.success('Период климата создан');
      }
      setIsModalOpen(false);
      setEditingPeriod(null);
      form.resetFields();
    } catch (error) {
      messageApi.error(extractApiError(error, 'Не удалось сохранить период климата'));
    }
  };

  const handleDelete = useCallback(async (period: GreenhouseClimatePeriod) => {
    try {
      await deletePeriod({
        sessionId,
        periodId: period.id,
        structuralObjectId: period.structuralObjectId,
      }).unwrap();
      messageApi.success('Период климата удалён');
    } catch (error) {
      messageApi.error(extractApiError(error, 'Не удалось удалить период климата'));
    }
  }, [deletePeriod, messageApi, sessionId]);

  // Install/removal window of the greenhouse structural object — from which day
  // (and until when) the covering affects the bed. Patches the plot plan.
  const handleActiveWindowChange = useCallback(async (activeFrom: string | null, activeTo: string | null) => {
    if (!selectedTarget || !saveId) {
      messageApi.error('Не удалось определить сейв/участок');
      return;
    }
    try {
      await patchPlotPlan({
        saveId,
        plotId: selectedTarget.plotId,
        fieldId: selectedTarget.fieldId,
        operations: [{
          op: 'updateStructuralObject',
          structuralObjectId: selectedTarget.id,
          activeFrom,
          activeTo,
        }],
      }).unwrap();
      setTargets((prev) => prev.map((t) => (t.id === selectedTarget.id ? { ...t, activeFrom, activeTo } : t)));
      messageApi.success('Срок действия теплицы обновлён');
    } catch (error) {
      messageApi.error(extractApiError(error, 'Не удалось обновить срок действия'));
    }
  }, [patchPlotPlan, selectedTarget, saveId, messageApi]);

  const handleHeatingEventSubmit = useCallback(async () => {
    if (!selectedTargetId) return;
    if (!saveId) {
      messageApi.error('Не удалось определить сейв симуляции');
      return;
    }

    try {
      const values = await heatingForm.validateFields();
      const payload = {
        saveId,
        sessionId,
        date: values.eventDate.format('YYYY-MM-DD'),
        action: values.action,
        structuralObjectId: selectedTargetId,
        properties: values.action === 'heating_on'
          ? (values.mode === 'follow_tech_card'
            ? { mode: 'follow_tech_card' }
            : {
                mode: 'manual',
                minC: values.minC ?? 5,
                maxC: values.maxC ?? 25,
                ventilationEnabled: true,
                ventilationMaxC: values.ventilationMaxC ?? 35,
              })
          : undefined,
      };

      await createSimEvent(payload).unwrap();
      messageApi.success(
        values.action === 'heating_on'
          ? 'Событие включения подогрева добавлено'
          : 'Событие выключения подогрева добавлено',
      );
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in (error as object)) {
        return;
      }
      messageApi.error(extractApiError(error, 'Не удалось добавить heating-событие'));
    }
  }, [createSimEvent, heatingForm, messageApi, saveId, selectedTargetId, sessionId]);

  const handleDeleteHeatingEvent = useCallback(async (event: SimEventLike) => {
    if (!saveId) {
      messageApi.error('Не удалось определить сейв симуляции');
      return;
    }
    try {
      await deleteSimEvent({
        saveId,
        sessionId,
        eventId: event.id,
        structuralObjectId: getSimEventStructuralObjectId(event) || selectedTargetId,
      }).unwrap();
      messageApi.success('Heating-событие удалено');
    } catch (error) {
      messageApi.error(extractApiError(error, 'Не удалось удалить heating-событие'));
    }
  }, [deleteSimEvent, messageApi, saveId, selectedTargetId, sessionId]);

  const temperatureChartOption = useMemo<any>(() => {
    const points = climateTimeseries?.points || [];
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: ['Наруж. Tmin', 'Наруж. Tmax', 'Внутр. Tmin', 'Внутр. Tmax'],
      },
      xAxis: { type: 'category', data: points.map((point) => point.date) },
      yAxis: { type: 'value', name: '°C', scale: true },
      dataZoom: [{ type: 'inside' }, { type: 'slider', start: 70, end: 100 }],
      series: [
        {
          name: 'Наруж. Tmin',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.tempMin),
        },
        {
          name: 'Наруж. Tmax',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.tempMax),
        },
        {
          name: 'Внутр. Tmin',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.tempMin),
        },
        {
          name: 'Внутр. Tmax',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.tempMax),
        },
      ],
    };
  }, [climateTimeseries?.points]);

  const humidityChartOption = useMemo<any>(() => {
    const points = climateTimeseries?.points || [];
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: ['Наруж. влажность', 'Внутр. влажность', 'Наруж. осадки', 'Внутр. осадки'],
      },
      xAxis: { type: 'category', data: points.map((point) => point.date) },
      yAxis: [
        { type: 'value', name: '%', min: 0, max: 100 },
        { type: 'value', name: 'мм', min: 0 },
      ],
      dataZoom: [{ type: 'inside' }, { type: 'slider', start: 70, end: 100 }],
      series: [
        {
          name: 'Наруж. влажность',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.externalWeather.humidityPct),
        },
        {
          name: 'Внутр. влажность',
          type: 'line',
          smooth: true,
          data: points.map((point) => point.effectiveWeather.humidityPct),
        },
        {
          name: 'Наруж. осадки',
          type: 'bar',
          yAxisIndex: 1,
          data: points.map((point) => point.externalWeather.precipitationMm),
        },
        {
          name: 'Внутр. осадки',
          type: 'bar',
          yAxisIndex: 1,
          data: points.map((point) => point.effectiveWeather.precipitationMm),
        },
      ],
    };
  }, [climateTimeseries?.points]);

  const periodColumns = useMemo(() => [
    {
      title: 'Период',
      key: 'period',
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space direction="vertical" size={2}>
          <Space wrap>
            <Text strong>{period.startDate}</Text>
            <Text type="secondary">→</Text>
            <Text>{period.endDate || 'без конца'}</Text>
            {climateStatus?.climatePeriod?.id === period.id && <Tag color="green">активен</Tag>}
          </Space>
          <Text type="secondary">{period.mode}</Text>
        </Space>
      ),
    },
    {
      title: 'Параметры',
      key: 'params',
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space direction="vertical" size={2}>
          <Text>Температура: {formatTempBand(period.internalTempC, period.internalTempAmplitudeC)}</Text>
          <Text>Влажность: {formatOptionalNumber(period.internalHumidityPct, '%')}</Text>
          <Space wrap size={4}>
            <Tag color={period.allowExternalPrecipitation ? 'blue' : 'default'}>
              {period.allowExternalPrecipitation ? 'Осадки снаружи проходят' : 'Осадки блокируются'}
            </Tag>
            <Tag color={period.isIsolatedZone ? 'gold' : 'default'}>
              {period.isIsolatedZone ? 'Изолированная зона' : 'Связана с внешним контуром'}
            </Tag>
            {period.ventilationEnabled && (
              <Tag color="cyan">Автопроветривание {formatOptionalNumber(period.ventilationMaxC, '°C')}</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 140,
      render: (_: unknown, period: GreenhouseClimatePeriod) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(period)}>
            Изменить
          </Button>
          <Popconfirm
            title="Удалить период климата?"
            description="Слой будет убран из сессии и симуляция пересчитается по обычным правилам."
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => handleDelete(period)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} loading={deletingPeriod}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [climateStatus?.climatePeriod?.id, deletingPeriod, handleDelete, openEditModal]);

  const heatingEventColumns = useMemo(() => [
    {
      title: 'Дата',
      key: 'date',
      width: 130,
      render: (_: unknown, event: SimEventLike) => <Text strong>{String(event.date || '—')}</Text>,
    },
    {
      title: 'Событие',
      key: 'action',
      width: 130,
      render: (_: unknown, event: SimEventLike) => {
        const action = getSimEventAction(event) as HeatingEventAction;
        return (
          <Tag color={action === 'heating_on' ? 'orange' : 'default'}>
            {HEATING_ACTION_LABELS[action] || action}
          </Tag>
        );
      },
    },
    {
      title: 'Параметры',
      key: 'params',
      render: (_: unknown, event: SimEventLike) => {
        const action = getSimEventAction(event);
        if (action === 'heating_off') {
          return <Text type="secondary">Подогрев отключается с этой даты</Text>;
        }

        const notes = parseHeatingEventNotes(event);
        const mode = typeof notes?.mode === 'string' ? notes.mode : 'manual';
        if (mode === 'follow_tech_card') {
          return <Tag color="gold">По техкарте</Tag>;
        }

        return (
          <Space wrap size={4}>
            <Tag>{HEATING_MODE_LABELS[mode] || mode}</Tag>
            {notes?.minC != null && <Tag>min {formatOptionalNumber(Number(notes.minC), '°C')}</Tag>}
            {notes?.maxC != null && <Tag>max {formatOptionalNumber(Number(notes.maxC), '°C')}</Tag>}
            {notes?.ventilationMaxC != null && <Tag>вент. {formatOptionalNumber(Number(notes.ventilationMaxC), '°C')}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 140,
      render: (_: unknown, event: SimEventLike) => (
        <Popconfirm
          title="Удалить heating-событие?"
          description="После удаления сессия пересчитает heating-слой для затронутого периода."
          okText="Удалить"
          cancelText="Отмена"
          onConfirm={() => handleDeleteHeatingEvent(event)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} loading={deletingHeatingEvent}>
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ], [deletingHeatingEvent, handleDeleteHeatingEvent]);

  return (
    <Card
      title="Климат теплицы"
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        <Space>
          {selectedTarget && (
            <Badge
              status={climateStatus?.climatePeriod ? 'processing' : 'default'}
              text={climateStatus?.climatePeriod ? 'есть активный климат-слой' : 'слой не активен'}
            />
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            disabled={!selectedTarget}
          >
            Новый период
          </Button>
        </Space>
      }
    >
      {messageContextHolder}

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Text strong>Теплица</Text>
          <Select
            value={selectedTargetId || undefined}
            onChange={setSelectedTargetId}
            placeholder={discoveringTargets ? 'Сканирую планы участков…' : 'Выберите теплицу'}
            loading={loadingFields || discoveringTargets}
            options={targets.map((target) => ({ value: target.id, label: target.label }))}
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
          />
          {selectedTarget && (
            <Space wrap size={6}>
              <Tag>{greenhouseSubtypeLabel(selectedTarget.subtype)}</Tag>
              <Tag color="blue">{selectedTarget.fieldName}</Tag>
              <Tag color="cyan">{selectedTarget.plotName}</Tag>
              <Tag color="default">ячеек: {selectedTarget.cellCount}</Tag>
            </Space>
          )}
          {selectedTarget && (
            <Space wrap size={8} align="center">
              <Text type="secondary">Действует с</Text>
              <DatePicker
                size="small"
                placeholder="с начала"
                disabled={patchingWindow}
                value={selectedTarget.activeFrom ? dayjs(selectedTarget.activeFrom) : null}
                onChange={(d) => handleActiveWindowChange(d ? d.format('YYYY-MM-DD') : null, selectedTarget.activeTo)}
              />
              <Text type="secondary">по</Text>
              <DatePicker
                size="small"
                placeholder="бессрочно"
                disabled={patchingWindow}
                value={selectedTarget.activeTo ? dayjs(selectedTarget.activeTo) : null}
                onChange={(d) => handleActiveWindowChange(selectedTarget.activeFrom, d ? d.format('YYYY-MM-DD') : null)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                — до установки грядка как открытый грунт
              </Text>
            </Space>
          )}
        </Space>

        {!loadingFields && !discoveringTargets && targets.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="В планах участков не найдено greenhouse/parnik"
            description="Сначала добавьте теплицу или парник в план участка, затем эта панель начнёт показывать её климат как отдельный слой симуляции."
          />
        )}

        {!selectedTarget ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Выберите теплицу, чтобы увидеть её текущий климатический слой и периоды"
          />
        ) : (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title="Эффективная Tmin/Tmax"
                    value={formatTempBand(
                      climateStatus?.effectiveWeather.tempMin ?? null,
                      climateStatus?.effectiveWeather.tempMax != null && climateStatus?.effectiveWeather.tempMin != null
                        ? climateStatus.effectiveWeather.tempMax - climateStatus.effectiveWeather.tempMin
                        : null,
                    )}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title="Влажность внутри"
                    value={climateStatus?.effectiveWeather.humidityPct ?? null}
                    precision={1}
                    suffix="%"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title="Осадки внутри"
                    value={climateStatus?.effectiveWeather.precipitationMm ?? null}
                    precision={2}
                    suffix="мм"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" loading={loadingStatus}>
                  <Statistic
                    title="Активные посадки"
                    value={climateStatus?.activePlantings.length ?? 0}
                  />
                </Card>
              </Col>
            </Row>

            <Card size="small" loading={loadingStatus}>
              <Descriptions column={{ xs: 1, sm: 2, lg: 4 }} size="small">
                <Descriptions.Item label="Дата снимка">{climateStatus?.date || simDate}</Descriptions.Item>
                <Descriptions.Item label="Покрытие">
                  {greenhouseSubtypeLabel(climateStatus?.layers.covering?.coveringSubtype || selectedTarget.subtype)}
                </Descriptions.Item>
                <Descriptions.Item label="Изоляция">
                  {climateStatus?.layers.greenhouseClimate?.isIsolatedZone || climateStatus?.layers.covering?.isIsolatedZone
                    ? 'Да'
                    : 'Нет'}
                </Descriptions.Item>
                <Descriptions.Item label="Обогрев">
                  {climateStatus?.layers.heating ? <Tag color="orange">активен</Tag> : 'нет'}
                </Descriptions.Item>
                <Descriptions.Item label="Климат-период">
                  {climateStatus?.climatePeriod
                    ? `${climateStatus.climatePeriod.startDate} → ${climateStatus.climatePeriod.endDate || 'без конца'}`
                    : 'нет'}
                </Descriptions.Item>
                <Descriptions.Item label="Наружная температура">
                  {formatOptionalNumber(climateStatus?.externalWeather.tempMin, '°C')} / {formatOptionalNumber(climateStatus?.externalWeather.tempMax, '°C')}
                </Descriptions.Item>
                <Descriptions.Item label="Внутренняя температура">
                  {formatOptionalNumber(climateStatus?.effectiveWeather.tempMin, '°C')} / {formatOptionalNumber(climateStatus?.effectiveWeather.tempMax, '°C')}
                </Descriptions.Item>
                <Descriptions.Item label="Контур осадков">
                  {climateStatus?.effectiveWeather.precipitationMm === climateStatus?.externalWeather.precipitationMm
                    ? 'следует внешней погоде'
                    : 'переопределён внутри'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title="Подогрев теплицы"
              extra={<Text type="secondary">ретроспективные heating-события сессии</Text>}
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="Подогрев управляется отдельными событиями внутри сессии"
                  description="Здесь можно поставить heating_on или heating_off на любую дату. Чтобы реально сдвинуть старт подогрева, удалите стартовое heating-событие из истории ниже и создайте новое на нужную дату."
                />

                <Row gutter={16}>
                  <Col xs={24} xl={10}>
                    <Card size="small" loading={loadingStatus}>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Состояние на дату снимка">
                          {currentHeating ? <Tag color="orange">подогрев активен</Tag> : <Tag>подогрев выключен</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Режим">
                          {currentHeating?.mode ? (HEATING_MODE_LABELS[currentHeating.mode] || currentHeating.mode) : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Конфигурация">
                          {buildHeatingSummary(currentHeating)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col xs={24} xl={14}>
                    <Card size="small" title="Добавить heating-событие">
                      <Form form={heatingForm} layout="vertical">
                        <Row gutter={12}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name="eventDate"
                              label="Дата"
                              rules={[{ required: true, message: 'Укажите дату heating-события' }]}
                            >
                              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item name="action" label="Действие" rules={[{ required: true }]}> 
                              <Select
                                options={[
                                  { value: 'heating_on', label: 'Включить подогрев' },
                                  { value: 'heating_off', label: 'Выключить подогрев' },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {heatingAction === 'heating_on' && (
                          <>
                            <Form.Item name="mode" label="Режим" rules={[{ required: true }]}> 
                              <Select
                                options={[
                                  { value: 'manual', label: 'Ручной диапазон' },
                                  { value: 'follow_tech_card', label: 'Следовать техкарте' },
                                ]}
                              />
                            </Form.Item>

                            {heatingMode === 'manual' ? (
                              <Row gutter={12}>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="minC" label="Min, °C">
                                    <InputNumber style={{ width: '100%' }} min={-20} max={50} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="maxC" label="Max, °C">
                                    <InputNumber style={{ width: '100%' }} min={-20} max={60} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item name="ventilationMaxC" label="Вентиляция, °C">
                                    <InputNumber style={{ width: '100%' }} min={0} max={80} />
                                  </Form.Item>
                                </Col>
                              </Row>
                            ) : (
                              <Alert
                                type="warning"
                                showIcon
                                message="Режим по техкарте"
                                description="После этой даты heating будет брать целевые параметры из активных посадок и их техкарт внутри выбранной теплицы."
                              />
                            )}
                          </>
                        )}

                        <Button
                          type="primary"
                          onClick={handleHeatingEventSubmit}
                          loading={creatingHeatingEvent}
                          disabled={!selectedTargetId}
                        >
                          Добавить heating-событие
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                </Row>

                <Table
                  rowKey="id"
                  columns={heatingEventColumns}
                  dataSource={heatingEvents}
                  size="small"
                  pagination={false}
                  loading={loadingHeatingEvents}
                  locale={{ emptyText: 'Для этой теплицы пока нет heating-событий в сессии' }}
                />
              </Space>
            </Card>

            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <Card size="small" title="Температура: наружный контур vs внутри" loading={loadingTimeseries}>
                  {!climateTimeseries?.points?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет временного ряда для выбранной теплицы" />
                  ) : (
                    <EChart option={temperatureChartOption} height={260} />
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card size="small" title="Влажность и осадки" loading={loadingTimeseries}>
                  {!climateTimeseries?.points?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет временного ряда для выбранной теплицы" />
                  ) : (
                    <EChart option={humidityChartOption} height={260} />
                  )}
                </Card>
              </Col>
            </Row>

            <Card
              size="small"
              title="Периоды климатического слоя"
              extra={<Text type="secondary">{loadingPeriods ? 'обновление…' : `${periods.length} периодов`}</Text>}
            >
              <Table
                rowKey="id"
                columns={periodColumns}
                dataSource={periods}
                size="small"
                pagination={false}
                loading={loadingPeriods}
                locale={{ emptyText: 'Для этой теплицы пока нет ручных климатических периодов' }}
              />
            </Card>
          </>
        )}
      </Space>

      <Modal
        title={editingPeriod ? 'Изменить климатический период' : 'Новый климатический период'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingPeriod(null);
        }}
        onOk={handleSubmit}
        confirmLoading={busyMutation}
        okText={editingPeriod ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="startDate"
            label="Начало"
            rules={[{ required: true, message: 'Укажите дату начала' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="endDate" label="Конец">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="internalTempC" label="Внутренняя температура, °C">
                <InputNumber style={{ width: '100%' }} min={-40} max={60} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="internalTempAmplitudeC" label="Амплитуда, °C">
                <InputNumber style={{ width: '100%' }} min={0} max={30} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="internalHumidityPct" label="Влажность, %">
            <InputNumber style={{ width: '100%' }} min={0} max={100} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ventilationEnabled" label="Автопроветривание" valuePropName="checked">
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(prev, next) => prev.ventilationEnabled !== next.ventilationEnabled}>
                {({ getFieldValue }) => (
                  <Form.Item name="ventilationMaxC" label="Порог, °C">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={-40}
                      max={80}
                      disabled={getFieldValue('ventilationEnabled') !== true}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="allowExternalPrecipitation" label="Пропускать внешние осадки" valuePropName="checked">
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
          <Form.Item name="isIsolatedZone" label="Изолированная зона" valuePropName="checked">
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default GreenhouseClimatePanel;
